
import React, { useMemo } from 'react';
import { 
  Sparkles, TrendingDown, Clock, Camera, BarChart3, Receipt, CreditCard, 
  ArrowRight, HardHat, BrickWall, Tag, Store, Calendar, Wallet 
} from 'lucide-react';
import { Project, ExpenseCategory } from '../types';
import { formatCurrency, calculateDaysOfWork } from '../utils/formatters';

interface DashboardViewProps {
  project: Project;
  setActiveTab: (tab: string) => void;
  onNewExpense: () => void;
  onEditExpense: (id: string) => void;
  onEditBudget: () => void;
}

export const DashboardView: React.FC<DashboardViewProps> = ({ 
  project, 
  setActiveTab, 
  onNewExpense,
  onEditExpense,
  onEditBudget
}) => {
  
  const stats = useMemo(() => {
    const totalSpent = project.expenses.filter(e => e.isPaid).reduce((a, b) => a + b.amount, 0);
    const totalUpcoming = project.expenses.filter(e => !e.isPaid).reduce((a, b) => a + b.amount, 0);
    
    const displayBudget = project.initialBudget || 0;
    const budgetForCalc = displayBudget > 0 ? displayBudget : 1;
    
    const progress = (totalSpent / budgetForCalc) * 100;
    
    // Projeção: se progresso > 0, extrapola. Senão é o orçamento.
    const projectionFinal = progress > 0 ? (totalSpent / (progress/100)) : displayBudget;
    
    // Economia só faz sentido se tiver orçamento definido
    const economyPercent = displayBudget > 0 ? ((displayBudget - projectionFinal) / displayBudget) * 100 : 0;

    const stagesSummary = project.stages.map(s => {
      const spent = project.expenses.filter(e => e.stageId === s.id && e.isPaid).reduce((a, b) => a + b.amount, 0);
      return { ...s, spent };
    });

    const supplierMap: Record<string, number> = {};
    project.expenses.forEach(e => {
      if (e.supplier && e.supplier !== 'N/A') {
        supplierMap[e.supplier] = (supplierMap[e.supplier] || 0) + e.amount;
      }
    });
    const topSuppliers = Object.entries(supplierMap)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4);
    
    const maxSupplierVal = topSuppliers.length > 0 ? Math.max(...topSuppliers.map(s => s[1])) : 1;

    const recentExpenses = [...project.expenses]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 5);

    return { 
        totalSpent, 
        totalUpcoming,
        totalBudget: displayBudget, 
        progress, 
        stagesSummary, 
        topSuppliers, 
        maxSupplierVal, 
        recentExpenses, 
        projectionFinal, 
        economyPercent 
    };
  }, [project.expenses, project.initialBudget, project.stages]);

  const daysOfWork = calculateDaysOfWork(project.startDate);

  const GRADIENTS = [
    { bg: 'bg-emerald-500', from: 'from-emerald-400', to: 'to-emerald-600', shadow: 'shadow-emerald-500/30' },
    { bg: 'bg-blue-500', from: 'from-blue-400', to: 'to-blue-600', shadow: 'shadow-blue-500/30' },
    { bg: 'bg-amber-500', from: 'from-amber-400', to: 'to-amber-600', shadow: 'shadow-amber-500/30' },
    { bg: 'bg-purple-500', from: 'from-purple-400', to: 'to-purple-600', shadow: 'shadow-purple-500/30' },
    { bg: 'bg-rose-500', from: 'from-rose-400', to: 'to-rose-600', shadow: 'shadow-rose-500/30' },
  ];

  return (
    <div className="space-y-12 animate-fadeIn pb-24">
      {/* Header Superior Centralizado */}
      <div className="text-center space-y-3">
        <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter uppercase leading-none">{project.name}</h2>
        <div className="inline-flex items-center gap-3 px-6 py-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-full border border-emerald-200 dark:border-emerald-800 shadow-sm hover:scale-105 transition-transform cursor-default">
          <Clock size={14} className="text-emerald-600" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400">
            {daysOfWork} Dias de Obra
          </span>
        </div>
      </div>

      {/* Card Financeiro Principal */}
      <div id="tour-summary" className="bg-gradient-to-br from-[#064e3b] to-[#065f46] text-white rounded-[2.5rem] p-8 sm:p-10 shadow-2xl relative overflow-hidden group hover:scale-[1.01] transition-transform duration-500">
         <div className="absolute top-0 right-0 p-12 opacity-10 group-hover:opacity-20 transition-opacity">
            <Sparkles size={120} />
         </div>
         
         <div className="flex justify-between items-start mb-6 relative z-10">
            <div className="space-y-1">
               <p className="text-xs font-medium opacity-80 uppercase tracking-widest">Total Realizado</p>
               <h4 className="text-5xl sm:text-6xl font-black tabular-nums tracking-tighter leading-none">
                 {formatCurrency(stats.totalSpent)}
               </h4>
               <div className="flex items-center gap-4 mt-2">
                 <p className="text-xs font-medium opacity-60">Meta: {formatCurrency(stats.totalBudget)}</p>
                 <button 
                  onClick={() => setActiveTab('expenses')}
                  className="flex items-center gap-1.5 px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full transition-all group/upcoming"
                 >
                    <Wallet size={12} className="text-emerald-400" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">
                      Previsto: {formatCurrency(stats.totalUpcoming)}
                    </span>
                 </button>
               </div>
            </div>
            <div className="text-right">
               <p className="text-4xl font-black text-emerald-400 leading-none">{Math.round(stats.progress)}%</p>
               <p className="text-[10px] font-bold opacity-60 uppercase tracking-widest mt-1">Executado</p>
            </div>
         </div>

         <div className="grid grid-cols-2 gap-4 mb-4 relative z-10">
            <div className="space-y-1">
               <p className="text-[10px] font-bold opacity-60 uppercase tracking-widest">Aproveitamento</p>
               <p className="text-2xl sm:text-3xl font-black">{Math.round(stats.progress)}%</p>
            </div>
            <div className="text-right space-y-1">
               <p className="text-[10px] font-bold opacity-60 uppercase tracking-widest">Projeção Final</p>
               <p className="text-2xl sm:text-3xl font-black">{formatCurrency(stats.projectionFinal)}</p>
            </div>
         </div>

         <div className="h-4 bg-emerald-950/50 rounded-full overflow-hidden shadow-inner mb-8 relative z-10">
            <div 
              className="h-full bg-[#14B84B] rounded-full transition-all duration-1000" 
              style={{ width: `${Math.min(stats.progress, 100)}%` }}
            ></div>
         </div>

         <div className="pt-6 border-t border-white/10 flex items-center justify-between relative z-10">
            <div className="flex items-center gap-2">
               <TrendingDown size={18} className={stats.economyPercent >= 0 ? "text-emerald-400" : "text-red-400"} />
               <span className="text-sm font-bold">
                 {stats.economyPercent >= 0 ? "Economia" : "Desvio"} de {Math.abs(stats.economyPercent).toFixed(1)}%
               </span>
            </div>
            <button 
              onClick={onEditBudget}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-black uppercase tracking-widest transition-all backdrop-blur-sm"
            >
              Alterar Orçamento
            </button>
         </div>
      </div>

      {/* Diário de Fotos Slider */}
      <div className="space-y-6">
         <div className="flex justify-between items-end px-4">
            <div>
              <h3 className="text-2xl font-black tracking-tighter uppercase">Diário de Obra</h3>
              <p className="text-xs font-bold text-slate-400">Os últimos registros visuais</p>
            </div>
            <button onClick={() => setActiveTab('photos')} className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400 hover:underline">Ver Mais</button>
         </div>
         <div className="flex gap-6 overflow-x-auto no-scrollbar snap-x px-4 py-2">
            {project.photos.length === 0 ? (
              <div className="w-full h-56 flex items-center justify-center bg-slate-100 dark:bg-slate-900/50 rounded-[3rem] border-4 border-dashed border-slate-200 dark:border-slate-800">
                 <p className="text-xs font-black uppercase text-slate-400 tracking-widest">Nenhuma foto ainda...</p>
              </div>
            ) : (
              project.photos.slice(0, 5).map(photo => (
                <div key={photo.id} className="snap-center shrink-0 w-80 aspect-[4/5] rounded-[3rem] overflow-hidden relative group shadow-2xl border-4 border-white dark:border-slate-800 transition-all hover:scale-[1.02] bg-slate-200 dark:bg-slate-800">
                   <img 
                    src={photo.url} 
                    className="w-full h-full object-cover opacity-0 transition-opacity duration-500" 
                    onLoad={(e) => (e.currentTarget as HTMLImageElement).classList.remove('opacity-0')}
                    alt={photo.room} 
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                   <div className="absolute top-6 right-6">
                      <div className="px-4 py-2 bg-white/20 backdrop-blur-md rounded-2xl text-[9px] font-black text-white uppercase tracking-widest border border-white/20">
                        {new Date(photo.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                      </div>
                   </div>
                   <div className="absolute bottom-8 left-8 right-8">
                      <p className="text-xs font-black text-emerald-400 uppercase tracking-widest mb-1">{photo.room}</p>
                      <p className="text-sm font-bold text-white line-clamp-1 opacity-80">{photo.description || 'Sem descrição'}</p>
                   </div>
                </div>
              ))
            )}
         </div>
      </div>

      {/* Ícones de Ação Rápida */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-4">
          {[
              { 
                  id: 'tour-add-expense', 
                  label: 'Novo Gasto', 
                  sub: 'Registrar',
                  icon: CreditCard, 
                  gradient: 'from-emerald-500 to-emerald-700', 
                  action: onNewExpense 
              },
              { 
                  id: 'photos_btn', 
                  label: 'Diário', 
                  sub: 'Fotos',
                  icon: Camera, 
                  gradient: 'from-blue-500 to-indigo-600', 
                  action: () => setActiveTab('photos') 
              },
              { 
                  id: 'reports_btn', 
                  label: 'Relatórios', 
                  sub: 'Análise',
                  icon: BarChart3, 
                  gradient: 'from-amber-400 to-orange-600', 
                  action: () => setActiveTab('stats') 
              },
              { 
                  id: 'extract_btn', 
                  label: 'Extrato', 
                  sub: 'Completo',
                  icon: Receipt, 
                  gradient: 'from-slate-700 to-slate-900', 
                  action: () => setActiveTab('expenses') 
              },
          ].map(item => (
              <button key={item.id} id={item.id} onClick={item.action} className={`relative group overflow-hidden rounded-[2rem] p-6 text-left shadow-lg hover:shadow-xl transition-all hover:-translate-y-1`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-100 transition-all`}></div>
                  <div className="relative z-10 flex flex-col h-full justify-between gap-6">
                      <div className="size-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-white border border-white/30 shadow-inner group-hover:scale-110 transition-transform duration-500">
                          <item.icon size={24} strokeWidth={2.5} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest mb-0.5">{item.sub}</p>
                          <p className="text-lg font-black text-white uppercase tracking-tight leading-none">{item.label}</p>
                      </div>
                  </div>
              </button>
          ))}
      </div>

      {/* Gastos por Etapa Carousel */}
      <div className="space-y-6 px-4">
         <div className="flex justify-between items-end">
            <h3 className="text-2xl font-black tracking-tighter uppercase">Gastos por Etapa</h3>
            <button onClick={() => setActiveTab('stats')} className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600 hover:underline">Ver Relatórios</button>
         </div>
         <div className="flex gap-4 overflow-x-auto no-scrollbar py-4 px-1 snap-x">
            {stats.stagesSummary.map((stage, idx) => {
                const theme = GRADIENTS[idx % GRADIENTS.length];
                return (
                    <div key={stage.id} className="snap-center shrink-0 w-60 bg-white dark:bg-slate-900 p-1 rounded-[2.5rem] shadow-lg group hover:-translate-y-1 transition-transform duration-300">
                        <div className="h-full w-full bg-white dark:bg-slate-900 rounded-[2.3rem] p-6 relative overflow-hidden border border-slate-100 dark:border-slate-800">
                            <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${theme.from} ${theme.to}`}></div>
                            
                            <div className="flex justify-between items-start mb-8">
                                <div className={`size-12 rounded-2xl bg-gradient-to-br ${theme.from} ${theme.to} flex items-center justify-center text-white shadow-lg ${theme.shadow}`}>
                                    <HardHat size={20} strokeWidth={2.5}/>
                                </div>
                                <div className="text-right">
                                     <div className="size-8 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center border border-slate-100 dark:border-slate-700 group-hover:bg-slate-100 dark:group-hover:bg-slate-700 transition-colors">
                                        <ArrowRight size={14} className="text-slate-400" />
                                     </div>
                                </div>
                            </div>
                            
                            <div className="relative z-10">
                                <h4 className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] mb-1 truncate">{stage.name}</h4>
                                <p className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{formatCurrency(stage.spent)}</p>
                            </div>
                        </div>
                    </div>
                );
            })}
         </div>
      </div>

      {/* Grid Inferior */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 px-4">
          <div className="space-y-6">
              <div className="flex justify-between items-center px-2">
                 <h3 className="text-xl font-black tracking-tighter uppercase">Últimos Lançamentos</h3>
                 <button onClick={() => setActiveTab('expenses')} className="size-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center hover:bg-emerald-100 hover:text-emerald-600 transition-colors">
                    <ArrowRight size={16} />
                 </button>
              </div>
              
              <div className="space-y-3">
                 {stats.recentExpenses.length === 0 ? (
                   <div className="p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl">
                      <Receipt className="mx-auto text-slate-300 mb-2" size={32} />
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Sem gastos recentes</p>
                   </div>
                 ) : (
                   stats.recentExpenses.map(expense => (
                     <div key={expense.id} onClick={() => onEditExpense(expense.id)} className="bg-white dark:bg-slate-900 p-4 rounded-3xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm hover:shadow-md transition-all group cursor-pointer">
                        <div className={`size-12 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${expense.isPaid ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20' : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20'}`}>
                           {expense.category === ExpenseCategory.MATERIALS ? <BrickWall size={20} /> : <Tag size={20} />}
                        </div>
                        <div className="flex-1 min-w-0">
                           <div className="flex justify-between items-start mb-1">
                              <p className="text-sm font-black text-slate-900 dark:text-white uppercase truncate pr-2">{expense.description}</p>
                              <p className="text-sm font-black text-slate-900 dark:text-white whitespace-nowrap">{formatCurrency(expense.amount)}</p>
                           </div>
                           <div className="flex justify-between items-center">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                 <Calendar size={10} /> {new Date(expense.date).toLocaleDateString('pt-BR')}
                              </p>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate max-w-[100px] text-right">
                                 {expense.supplier}
                              </p>
                           </div>
                        </div>
                     </div>
                   ))
                 )}
              </div>
          </div>

          <div className="space-y-6">
              <h3 className="text-xl font-black tracking-tighter uppercase px-2">Top Fornecedores</h3>
              <div className="bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] shadow-lg border border-slate-100 dark:border-slate-800 space-y-6">
                 {stats.topSuppliers.length === 0 ? (
                   <div className="text-center py-8">
                      <Store className="mx-auto text-slate-300 mb-2" size={32} />
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Nenhum fornecedor ainda</p>
                   </div>
                 ) : (
                   stats.topSuppliers.map(([name, amount], idx) => (
                     <div key={idx} className="space-y-2">
                        <div className="flex justify-between items-end">
                           <div className="flex items-center gap-2">
                              <div className="size-6 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-[10px] font-black text-slate-500">
                                 {idx + 1}
                              </div>
                              <span className="text-xs font-black uppercase text-slate-700 dark:text-slate-300 tracking-wide">{name}</span>
                           </div>
                           <span className="text-xs font-black text-slate-900 dark:text-white">{formatCurrency(amount)}</span>
                        </div>
                        <div className="h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                           <div 
                             className="h-full bg-emerald-500 rounded-full" 
                             style={{ width: `${(amount / stats.maxSupplierVal) * 100}%` }}
                           ></div>
                        </div>
                     </div>
                   ))
                 )}
              </div>
          </div>
      </div>
    </div>
  );
};
